import type { ReactElement, SVGProps } from 'react';

/**
 * Iconos del chrome backoffice — copia 1:1 de
 * claude-design/claude-design-v4/orborix/project/backoffice/assets/chrome.js
 * (líneas 22-37). Cada path usa `currentColor` y `viewBox="0 0 16 16"`.
 *
 * Si agregás un icono nuevo, agregalo también en el bundle v4 para
 * mantener paridad.
 */
export type IconName =
  | 'grid'
  | 'layers'
  | 'users'
  | 'bar'
  | 'plug'
  | 'alert'
  | 'cog'
  | 'tag'
  | 'shield'
  | 'wrench'
  | 'search'
  | 'user'
  | 'trace'
  | 'session'
  | 'mail';

const PATHS: Record<IconName, ReactElement> = {
  grid: <path d="M2 2h5v5H2zM9 2h5v5H9zM2 9h5v5H2zM9 9h5v5H9z" fill="none" stroke="currentColor" strokeWidth="1.3" />,
  layers: (
    <path
      d="M8 1.5l6 3-6 3-6-3 6-3zM2 8l6 3 6-3M2 11l6 3 6-3"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinejoin="round"
    />
  ),
  users: (
    <>
      <circle cx="6" cy="6" r="2.2" fill="none" stroke="currentColor" strokeWidth="1.3" />
      <path d="M2 13c0-2 1.8-3.5 4-3.5s4 1.5 4 3.5" fill="none" stroke="currentColor" strokeWidth="1.3" />
      <circle cx="11" cy="5" r="1.8" fill="none" stroke="currentColor" strokeWidth="1.3" />
      <path d="M9.5 11c.5-1.4 1.8-2.3 3.5-2.3" fill="none" stroke="currentColor" strokeWidth="1.3" />
    </>
  ),
  bar: (
    <path
      d="M2 13V8M6 13V4M10 13V10M14 13V6"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
    />
  ),
  plug: (
    <path
      d="M5 2v3M11 2v3M3.5 5h9v3a4.5 4.5 0 0 1-9 0V5zM8 12.5V15"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
    />
  ),
  alert: (
    <>
      <path
        d="M8 2l6.5 11.5h-13L8 2z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinejoin="round"
      />
      <path d="M8 6v3M8 11v.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </>
  ),
  cog: (
    <>
      <circle cx="8" cy="8" r="2.2" fill="none" stroke="currentColor" strokeWidth="1.3" />
      <path
        d="M8 2v2M8 12v2M2 8h2M12 8h2M4 4l1.4 1.4M10.6 10.6L12 12M4 12l1.4-1.4M10.6 5.4L12 4"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
      />
    </>
  ),
  tag: (
    <>
      <path d="M8 2H2v6l7 7 6-6-7-7z" fill="none" stroke="currentColor" strokeWidth="1.3" />
      <circle cx="5" cy="5" r="0.8" fill="currentColor" />
    </>
  ),
  shield: (
    <path
      d="M8 2l5 2v4c0 3-2.5 5.5-5 6-2.5-.5-5-3-5-6V4l5-2z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
    />
  ),
  wrench: (
    <path
      d="M10 3a3 3 0 0 1 3 4l-7 7-2-2 7-7a3 3 0 0 1 -1-2z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
    />
  ),
  search: (
    <>
      <circle cx="7" cy="7" r="4.5" fill="none" stroke="currentColor" strokeWidth="1.3" />
      <path d="M10.5 10.5L13 13" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </>
  ),
  user: (
    <>
      <circle cx="8" cy="6" r="2.5" fill="none" stroke="currentColor" strokeWidth="1.3" />
      <path d="M3 13c0-2.5 2.5-4 5-4s5 1.5 5 4" fill="none" stroke="currentColor" strokeWidth="1.3" />
    </>
  ),
  trace: (
    <>
      <circle cx="3" cy="8" r="1.5" fill="currentColor" />
      <circle cx="13" cy="8" r="1.5" fill="currentColor" />
      <path d="M4.5 8H11.5" stroke="currentColor" strokeWidth="1.3" />
      <circle cx="8" cy="3" r="1.2" fill="currentColor" />
      <path d="M8 4.2v3" stroke="currentColor" strokeWidth="1.3" />
    </>
  ),
  session: (
    <>
      <rect x="2" y="3" width="12" height="10" rx="2" fill="none" stroke="currentColor" strokeWidth="1.3" />
      <path d="M2 6h12" stroke="currentColor" strokeWidth="1.3" />
    </>
  ),
  mail: (
    <>
      <rect x="2" y="3.5" width="12" height="9" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.3" />
      <path
        d="M2.5 4.5L8 9l5.5-4.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinejoin="round"
      />
    </>
  ),
};

interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'name'> {
  name: IconName;
}

export function Icon({ name, ...rest }: IconProps): ReactElement {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" {...rest}>
      {PATHS[name]}
    </svg>
  );
}
